import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/lib/supabase";

interface AdminRow {
  id: string;
  user_id: string | null;
  email: string;
  full_name: string | null;
  role: string;
  created_at: string;
}

export default function AdminsManager() {
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("admin");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<AdminRow | null>(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await supabase.functions.invoke("manage-admin", {
        body: { action: "list" },
      });
      if (err) throw err;
      if (data?.error) throw new Error(data.error);
      setAdmins(data?.admins || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data, error: err } = await supabase.functions.invoke("manage-admin", {
        body: { action: "add", email, full_name: fullName, role },
      });
      if (err) throw err;
      if (data?.error) throw new Error(data.error);
      setSuccess(`Admin "${email}" added. They can now sign in with this email address.`);
      setEmail("");
      setFullName("");
      setRole("admin");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add admin");
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setError("");
    setSuccess("");
    try {
      const { data, error: err } = await supabase.functions.invoke("manage-admin", {
        body: { action: "remove", id: removing.id },
      });
      if (err) throw err;
      if (data?.error) throw new Error(data.error);
      setSuccess(`Admin "${removing.email}" removed.`);
      setRemoving(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove admin");
      setRemoving(null);
    }
  };

  return (
    <div>
      <h2 className="font-heading text-2xl font-semibold uppercase text-brand-dark">Admins</h2>
      <p className="mt-1 text-sm text-neutral-500">
        Control who can access this dashboard and manage the website.
      </p>

      {error && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => setError("")} className="text-red-700">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
      {success && (
        <div className="mt-4 flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-4 py-3">
          <span className="text-sm text-green-700">{success}</span>
          <button onClick={() => setSuccess("")} className="text-green-700">
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg border border-neutral-200 bg-white">
          <div className="border-b border-neutral-200 px-5 py-4">
            <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">
              Current Admins ({admins.length})
            </h3>
          </div>
          {loading ? (
            <div className="flex h-48 items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-2xl text-brand-gold"></i>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {admins.map((a) => (
                <li key={a.id} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-dark text-brand-gold">
                      <i className="ri-user-line text-lg"></i>
                    </span>
                    <div>
                      <p className="font-medium text-brand-dark">{a.full_name || "—"}</p>
                      <p className="text-xs text-neutral-500">{a.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        a.role === "owner" ? "bg-brand-gold/20 text-brand-dark" : "bg-neutral-100 text-neutral-600"
                      }`}
                    >
                      {a.role}
                    </span>
                    <button
                      onClick={() => setRemoving(a)}
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-red-500 hover:bg-red-50"
                      title="Remove"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </li>
              ))}
              {admins.length === 0 && (
                <li className="px-5 py-10 text-center text-neutral-400">No admins yet.</li>
              )}
            </ul>
          )}
        </div>

        <form
          onSubmit={handleAdd}
          className="rounded-lg border border-neutral-200 bg-white p-5 h-fit"
        >
          <h3 className="font-heading text-base font-semibold uppercase text-brand-dark">Add Admin</h3>
          <p className="mt-1 text-xs text-neutral-500">
            The new admin must create an account with this exact email to sign in.
          </p>

          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-dark">Email *</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@topgearautospares.com"
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                className="mt-1.5 w-full rounded-md border border-neutral-300 px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:border-brand-gold focus:outline-none"
              >
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white disabled:opacity-60 whitespace-nowrap"
            >
              {saving && <i className="ri-loader-4-line animate-spin"></i>}
              {saving ? "Adding…" : "Add Admin"}
            </button>
          </div>
        </form>
      </div>

      {removing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
              <i className="ri-delete-bin-line text-2xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-brand-dark">Remove admin?</h3>
            <p className="mt-2 text-sm text-neutral-500">
              “{removing.email}” will lose access to this dashboard.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setRemoving(null)}
                className="rounded-md border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-50 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemove}
                className="rounded-md bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 whitespace-nowrap"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}